import { useState } from 'react';
import { PartyPopper, MapPin, Clock, FileText, Video, CalendarCheck } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useInterview } from '../../../hooks/queries/useInterviews';
import { useSelectInterviewSlot } from '../../../hooks/mutations/useInterviewMutations';

interface CandidateInterviewViewProps {
  interviewId: string;
}

function formatSlotLabel(iso: string): { date: string; time: string } {
  const parsed = parseISO(iso);
  return {
    date: format(parsed, 'EEE, MMM d'),
    time: format(parsed, 'h:mm a'),
  };
}

export default function CandidateInterviewView({ interviewId }: CandidateInterviewViewProps) {
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const { data: interview, isLoading, isError } = useInterview(interviewId);
  const selectSlot = useSelectInterviewSlot();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base text-gray-500">Loading interview details...</p>
        </div>
      </div>
    );
  }

  if (isError || !interview) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900 mb-2">Interview Not Found</p>
          <p className="text-base text-gray-500">
            This interview invitation is no longer available or the link may be invalid.
          </p>
        </div>
      </div>
    );
  }

  // Build slot pills from availableSlots array (preferred) or scheduledAt fallback
  const rawSlots: string[] = interview.availableSlots?.length
    ? interview.availableSlots
    : interview.scheduledAt
    ? [interview.scheduledAt]
    : [];

  const availableSlots: { date: string; time: string; isoDate: string; index: number }[] = rawSlots.map(
    (iso, index) => ({
      ...formatSlotLabel(iso),
      isoDate: iso,
      index,
    })
  );

  const isVirtual = interview.type === 'virtual';

  const handleConfirm = () => {
    if (selectedSlotIndex === null) return;
    const selectedSlot = availableSlots[selectedSlotIndex];
    if (!selectedSlot) return;
    selectSlot.mutate(
      { interviewId, payload: { selectedSlot: selectedSlot.isoDate } },
      {
        onSuccess: () => setConfirmed(true),
      }
    );
  };

  if (confirmed) {
    const confirmedSlot = selectedSlotIndex !== null ? availableSlots[selectedSlotIndex] : null;
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CalendarCheck className="w-10 h-10 text-[#0156FF]" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            You're All Set!
          </h1>
          <p className="text-base text-gray-600 mb-6">
            Your interview for <span className="font-semibold">{interview.role}</span> is confirmed.
            We look forward to meeting you, {interview.candidateName}.
          </p>
          {confirmedSlot && (
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 text-left">
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Your time</p>
              <p className="text-base font-bold text-gray-900">{confirmedSlot.date}</p>
              <p className="text-base text-gray-700">{confirmedSlot.time}</p>
            </div>
          )}
          <p className="text-sm text-gray-400 mt-6">You can close this page now.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto pb-32">
        {/* Header */}
        <header className="bg-white px-6 pt-8 pb-6 text-center">
          <PartyPopper className="w-16 h-16 text-[#0156FF] mx-auto mb-4" strokeWidth={2} />
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Congratulations, {interview.candidateName}!
          </h1>
          <p className="text-base text-gray-600">
            You've been selected to interview for{' '}
            <span className="font-semibold">{interview.role}</span>.
          </p>
        </header>

        {/* Details Card */}
        <section className="mx-4 mb-6 bg-gray-50 rounded-2xl p-5 space-y-4">
          {/* Interview type + location/link */}
          <div className="flex items-start gap-3">
            {isVirtual ? (
              <Video className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
            ) : (
              <MapPin className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {isVirtual ? 'Virtual Interview' : 'In-Person Interview'}
              </p>
              {isVirtual ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  A video link will be sent to you before the interview.
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  Location details will be confirmed with your time slot.
                </p>
              )}
            </div>
          </div>

          {/* Duration */}
          {interview.duration && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-gray-900">{interview.duration} minutes</p>
            </div>
          )}

          {/* Scheduled time (already confirmed) */}
          {interview.scheduledAt && !interview.availableSlots?.length && (
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-sm text-gray-900">
                {format(parseISO(interview.scheduledAt), "EEEE, MMMM d 'at' h:mm a")}
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-gray-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-sm text-gray-900">
              {isVirtual
                ? 'Have your ID handy and join from a quiet space.'
                : 'Bring your ID and a copy of your resume.'}
            </p>
          </div>
        </section>

        {/* Slot Selector */}
        {availableSlots.length > 0 && (
          <section className="px-4">
            <h2 className="text-lg font-bold text-gray-900 mb-1">Pick a Time</h2>
            <p className="text-sm text-gray-500 mb-4">Choose the slot that works best for you.</p>
            <div className="space-y-3">
              {availableSlots.map((slot) => {
                const selected = selectedSlotIndex === slot.index;
                return (
                  <button
                    key={slot.index}
                    type="button"
                    onClick={() => setSelectedSlotIndex(slot.index)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border-2 transition-all text-left ${
                      selected
                        ? 'border-[#0156FF] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div>
                      <p className={`text-sm font-bold ${selected ? 'text-[#0156FF]' : 'text-gray-900'}`}>
                        {slot.date}
                      </p>
                      <p className={`text-sm ${selected ? 'text-blue-600' : 'text-gray-600'}`}>
                        {slot.time}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selected ? 'border-[#0156FF] bg-[#0156FF]' : 'border-gray-300'
                    }`}>
                      {selected && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* Bottom Action Bar (Fixed) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4 shadow-lg">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={availableSlots.length > 0 ? selectedSlotIndex === null : false}
          className={`w-full h-14 rounded-full text-lg font-bold transition-all ${
            availableSlots.length === 0 || selectedSlotIndex !== null
              ? 'bg-[#0156FF] text-white hover:bg-blue-700'
              : 'bg-[#0156FF] text-white opacity-50 cursor-not-allowed'
          }`}
        >
          {selectSlot.isPending ? 'Confirming...' : 'Confirm Interview'}
        </button>
        {selectSlot.isError && (
          <p className="text-red-500 text-sm text-center mt-2">
            Something went wrong. Please try again.
          </p>
        )}
        <button
          type="button"
          className="w-full text-sm text-gray-500 hover:text-gray-700 mt-3"
          onClick={() => {
            window.location.href = `mailto:support@passcom.com?subject=Interview Reschedule Request&body=Hi, I need to reschedule my interview (ID: ${interviewId}). Please send me alternative times.`;
          }}
        >
          None of these work for me
        </button>
      </div>
    </div>
  );
}
